import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ShoppingCart, Menu, X, User, Shield } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const StoreNavigation = ({ cartItemCount = 0 }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAdminAuth();

  const navLinks = [
    { to: '/store', label: 'Home' },
    { to: '/shop', label: 'Shop' },
    { to: '/fit', label: 'Fit Guide' },
    { to: '/about-vesta', label: 'About' },
    { to: '/wholesale', label: 'Trade' },
    { to: '/blog', label: 'Community' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b-4 border-[#004D51]">
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/store" className="flex items-center">
            <img 
              src="/vesta-secondary-teal-flame.png" 
              alt="Vesta Workwear" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-bold uppercase tracking-wide transition-colors ${
                  isActive(link.to) 
                    ? 'text-[#DB5A14]' 
                    : 'text-[#545759] hover:text-[#DB5A14]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Side - Cart & Account */}
          <div className="flex items-center space-x-4">
            {/* Admin link (only when logged in) */}
            {isAuthenticated && (
              <Link
                to="/admin/blog"
                data-testid="nav-admin-link"
                className="hidden lg:flex items-center text-xs font-bold uppercase tracking-wide text-[#004D51] hover:text-[#DB5A14] transition-colors"
              >
                <Shield className="w-4 h-4 mr-1" /> Admin
              </Link>
            )}

            {/* Account Icon */}
            <Link
              to="/account"
              className="hidden md:block text-[#545759] hover:text-[#DB5A14] transition-colors"
            >
              <User className="w-6 h-6" />
            </Link>

            {/* Cart */}
            <Link to="/cart" className="relative">
              <Button 
                variant="ghost" 
                size="icon"
                className="text-[#004D51] hover:text-[#DB5A14] hover:bg-transparent"
              >
                <ShoppingCart className="w-6 h-6" />
                {cartItemCount > 0 && (
                  <span data-testid="cart-badge" className="absolute -top-1 -right-1 bg-[#DB5A14] text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {cartItemCount}
                  </span>
                )}
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-[#004D51]"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-3 text-base font-bold uppercase tracking-wide ${
                  isActive(link.to) 
                    ? 'text-[#DB5A14]' 
                    : 'text-[#545759]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/account"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-3 text-base font-bold uppercase tracking-wide text-[#545759]"
            >
              Account
            </Link>
            {isAuthenticated && (
              <Link
                to="/admin/blog"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="nav-admin-link-mobile"
                className="flex items-center py-3 text-base font-bold uppercase tracking-wide text-[#004D51]"
              >
                <Shield className="w-4 h-4 mr-2" /> Admin
              </Link>
            )}
          </nav>
        )}
      </div>
    </header>
  );
};

export default StoreNavigation;